import styled from 'styled-components';
import PrevArrowIcon from '../../../../new-components/svg-icons/PrevArrowIcon';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { getImagePath, getPageLocation } from '../../../../helpers/common';
import ImageWrapper from '../../../../new-components/ImageWrapper';
import Slider from '../../../../new-components/Slider';
import Link from 'next/link';
import { logEvent } from '../../../../new-lib/analytics';
import LaunchesBannerHeader from './LaunchesBannerHeader';
import { LaunchesProps } from '../../launch/types';

interface LaunchesBannerContainerProps {
  locale?: string;
  path?: string;
}

const LaunchesBanner = ({ launches }: LaunchesProps) => {
  const { t } = useTranslation('launches');
  const router = useRouter();
  const { locale } = useRouter();

  const getLaunchesBanner = (launches: any[]) => {
    const handleExploreButtonClick = () => {
      logEvent.click('lnch_view_all', getPageLocation(router.asPath), 'launch');
    };

    const handleLaunchBannerClick = (launch: any) => {
      logEvent.click('lnch_click', getPageLocation(router.asPath), 'launch', {
        cmpnd_id: launch?.id,
        cmpnd_name: launch?.title,
        area_id: launch?.area?.id,
        area_name: launch?.area?.name
      });
    };

    const getSingleLaunchBanner = (launch: any) => (
      <SingleLaunchBannerContainer locale={locale} path={router.asPath}>
        <Link
          href={`/compound/${launch?.slug}`}
          className="banner-image"
          onClick={() => handleLaunchBannerClick(launch)}
        >
          <ImageWrapper
            src={getImagePath(
              launch?.imageUrl || launch?.marketing_image_square_path
            )}
            alt="banner-image"
            objectFit="cover"
            fill
            style={{
              width: '100%',
              border: 'none'
            }}
            imageSize="high"
            priority
          />
        </Link>
        <div className="action-side">
          <div className="title">
            <h3>
              <span>{t('new_launches')}</span>
            </h3>
            <div className="border"></div>
            <p>{t('launches_subtitle')}</p>
          </div>

          <Link
            className="launch-btn"
            href="/new-launches"
            prefetch
            onClick={handleExploreButtonClick}
          >
            {t('explore_more')}

            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M12.0249 4.94043L17.0832 9.99876L12.0249 15.0571"
                stroke="#015C9A"
                stroke-width="1.4"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M2.9165 10H16.9415"
                stroke="#015C9A"
                stroke-width="1.4"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </Link>
        </div>
      </SingleLaunchBannerContainer>
    );

    const getMultipleLaunchesBanner = () => {
      const getSliderChildren = () =>
        launches?.map((launch, index) => (
          <Link
            href={`/compound/${launch.slug}`}
            prefetch={index > 2}
            key={`${launch?.id}-${index}`}
            className="banner-image"
            onClick={() => handleLaunchBannerClick(launch)}
          >
            <ImageWrapper
              src={getImagePath(
                launch?.imageUrl || launch?.marketing_image_square_path
              )}
              alt="banner-image"
              objectFit="cover"
              width={393}
              height={221}
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '8px',
                border: '1px solid #E6E6E6',
                cursor: 'pointer'
              }}
              priority={index < 3}
              onClick={() => handleLaunchBannerClick(launch)}
              imageSize="medium"
              sizes="80vw"
            />
          </Link>
        ));

      const launchesSliderProps = {
        width: '393px',
        height: '221px',
        slidesPerView: 'auto' as 'auto',
        spaceBetweenSlides: 16,
        slidesPerGroup: 1,
        children: getSliderChildren()
      };

      return (
        <MultipleLaunchesBannerContainer>
          <LaunchesBannerHeader />
          <Slider {...launchesSliderProps} />
        </MultipleLaunchesBannerContainer>
      );
    };

    if (launches.length === 1) {
      return getSingleLaunchBanner(launches[0]);
    } else {
      return getMultipleLaunchesBanner();
    }
  };
  return (
    <Container data-test="launches-banner">
      {getLaunchesBanner(launches)}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  /* margin: 40px 0; */
`;

const SingleLaunchBannerContainer = styled.div<LaunchesBannerContainerProps>`
  width: 100%;
  display: flex;
  align-items: center;
  border-radius: 18px;
  box-shadow: 0px 1px 9px 0px rgba(188, 188, 188, 0.16);
  background: #fff;

  .banner-image {
    position: relative;
    height: 200px;
    width: 45%;
    border-start-end-radius: 0px;
    border-end-end-radius: 0px;
    border-end-start-radius: 18px;
    border-start-start-radius: 18px;
    overflow: hidden;
    z-index: 10;
  }

  .action-side {
    display: flex;
    flex-direction: column;
    align-items: start;
    gap: 16px;
    padding: 24px 30px;
    .title {
      display: flex;
      flex-direction: column;
      align-items: start;
      gap: 4px;
      h3 {
        color: #ab8850;
        font-family: materialSemiBold;
        font-size: 20px;
        line-height: 22px;
        text-transform: capitalize;
      }
      .border {
        background-color: #ab8850;
        height: 1.5px;
        width: 40px;
        margin: 8px 0;
      }
      p {
        color: #3d3d3d;
        font-family: materialLight;
        font-size: 14px;
        line-height: 150%;
        max-width: 280px;
      }
    }

    .launch-btn {
      font-family: materialMedium;
      display: flex;
      flex-direction: row;
      gap: 8px;
      align-items: center;
      height: 38px;
      padding: 0px 16px;
      border-radius: 100px;
      background: #e4ebf2;
      color: #015c9a;
      svg {
        transform: ${props => (props.locale == 'ar' ? 'rotate(180deg)' : '')};
      }
    }
  }
`;

const MultipleLaunchesBannerContainer = styled.div<LaunchesBannerContainerProps>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-x: auto;

  .banner-image {
    width: 393px;
    height: 221px;
  }
`;

export default LaunchesBanner;
